import type { ApiNode } from "@/workflow_api/types/workflow";
import type { Variable, NodeVariable } from "../../types";
import type { DocumentField, DocumentTemplate } from "../types";
import type { VariableFileName } from "../context";

interface SerializedInput {
  name: string;
  value: string | number | boolean | null | VariableFileName;
  type: string;
  connection?: string;
  order: number;
  field?: string;
  output_name?: string; // for interoperability node outputs - contains the endpoint field key (e.g., "last_name")
  global_type?:
    | "APPLICANT"
    | "NODE_EXECUTION_START"
    | "NODE_EXECUTION_FINISH"
    | "CURRENT_TIME";
}

/**
 * Mapeo de tipos de variables del frontend al backend
 */
const mapVariableTypeToBackend = (frontendType: string): string => {
  switch (frontendType.toLowerCase()) {
    case "text":
    case "string":
      return "STRING";
    case "number":
    case "integer":
      return "INTEGER";
    case "float":
      return "FLOAT";
    case "date":
    case "datetime":
      return "DATE";
    case "boolean":
    case "bool":
      return "BOOLEAN";
    default:
      return "STRING";
  }
};

/**
 * Serializa los campos del documento siguiendo el patrón de calculated-field
 * @param fields - Array de DocumentField objects
 * @param variables - Variables disponibles para resolver conexiones
 * @param template - Template object containing variables with mapping_name
 * @returns Array de inputs serializados
 */
export const getSerializedFields = (
  fields: DocumentField[],
  variables?: NodeVariable[],
  template?: DocumentTemplate,
): SerializedInput[] => {
  const serializedInputs: SerializedInput[] = [];

  // Helper para obtener nodeId de una variable
  const getNodeIdFromVariable = (variableId: string): string | undefined => {
    if (!variables) return undefined;
    for (const nodeVar of variables) {
      const found = nodeVar.variables.find((v) => v.id === variableId);
      if (found) return nodeVar.nodeId;
    }
    return undefined;
  };

  // Helper para verificar si una variable es de un nodo de interoperabilidad
  const isInteroperabilityNodeVariable = (
    nodeId: string | undefined,
  ): boolean => {
    if (!variables || !nodeId) return false;
    const nodeVariable = variables.find((nv) => nv.nodeId === nodeId);
    // Check if the node type is interoperability-node
    return (
      nodeVariable?.type === "interoperability-node" ||
      nodeVariable?.type === "n8n-node"
    );
  };

  /**
   * Verifica si un nodo es de tipo expediente.
   */
  const isExpedienteNodeVariable = (nodeId: string | undefined): boolean => {
    if (!variables || !nodeId) return false;
    const nodeVariable = variables.find((nv) => nv.nodeId === nodeId);
    return nodeVariable?.type === "expedient-node";
  };

  fields.forEach((field, index) => {
    // Serializar TODOS los campos, incluso los vacíos
    let value: string | number | boolean | null = null;
    let connection: string | undefined = undefined;
    let fieldId: string | undefined = undefined;
    let output_name: string | undefined = undefined;

    // Buscar el mapping_name en las variables de la plantilla
    const templateVariable = template?.variables?.find(
      (variable) => variable.mapping_name === field.name,
    );

    // Usar mapping_name si está disponible en la plantilla, sino usar field.name
    const fieldName = templateVariable?.mapping_name || field.name;

    let variable: Variable | undefined = undefined;
    let fullVariable: Variable | undefined = undefined;

    if (field.inputType === "variable" && typeof field.value === "object") {
      // Es una variable conectada
      variable = field.value as Variable;

      fullVariable = variables
        ?.flatMap((nodeVar) => nodeVar.variables)
        .find((v) => v.id === variable!.id);

      if (fullVariable?.global_type) {
        if (
          fullVariable.global_type === "APPLICANT" ||
          fullVariable.global_type === "CURRENT_TIME"
        ) {
          value = fullVariable.id || null;
        } else {
          value = fullVariable.node_id || null;
          connection = undefined;
          fieldId = undefined;
        }
      } else if (fullVariable?.field_id && fullVariable?.output_name) {
        connection = fullVariable.node_id;
        fieldId = fullVariable.field_id;
        value = null;
        output_name = fullVariable.output_name;
      } else {
        value = null;
        connection = getNodeIdFromVariable(variable.id);

        if (fullVariable) {
          if (fullVariable.id === fullVariable.node_id) {
            fieldId = undefined;
          } else {
            fieldId = variable.id;
          }
        } else {
          fieldId = variable.id;
        }
      }
    } else {
      if (field.value === "" || field.value == null) {
        value = null;
      } else {
        value = field.value as string;
      }
      connection = undefined;
      fieldId = undefined;
    }

    const serializedInput: SerializedInput = {
      name: fieldName, // Usar el mapping_name en lugar de `field_${field.name}_${index}`
      value,
      type: mapVariableTypeToBackend(field.type),
      connection,
      order: index,
    };

    if (connection && fieldId) {
      if (variable && isInteroperabilityNodeVariable(connection)) {
        serializedInput.output_name =
          fullVariable?.output_name || variable.output_name;
      } else if (
        isExpedienteNodeVariable(connection) &&
        (fullVariable?.output_name || variable?.output_name)
      ) {
        // Para nodo expediente: NO enviar field, solo connection y output_name
        // El backend usa output_name para identificar el campo del expediente
        serializedInput.output_name =
          fullVariable?.output_name || variable?.output_name;
      } else {
        serializedInput.field = fieldId;
      }
    }

    if (output_name) {
      serializedInput.output_name = output_name;
    }

    if (fullVariable?.global_type) {
      serializedInput.global_type = fullVariable.global_type;
    }

    serializedInputs.push(serializedInput);
  });

  return serializedInputs;
};

/**
 * Serialize the document generator node to be saved in the database
 * Updated to use the same input structure as calculated-field
 * Now includes connections for "Resultado" and "Error" outputs
 *
 * @param currentNode - The current API node
 * @param title - Display name for the node
 * @param flowId - ID of the workflow flow
 * @param templateId - ID of the selected template
 * @param templateName - Name of the selected template
 * @param _fileName - Name of the generated file
 * @param fields - Array of document fields with their values
 * @param variableName - Name of the output variable
 * @param variables - Available variables for resolving connections
 * @param template - Template object containing variables with mapping_name
 * @returns Serialized node data for the API
 */
export const getSerializedDocumentGeneratorNode = (
  currentNode: ApiNode,
  title: string,
  flowId: string,
  templateId: string,
  templateName: string,
  fields: DocumentField[],
  variableName: VariableFileName,
  //_fileName?: string,
  variables?: NodeVariable[],
  template?: DocumentTemplate,
  manual_continue?: boolean,
) => {
  // Obtener los inputs serializados de los campos
  const fieldInputs = getSerializedFields(fields, variables, template);

  // Crear inputs adicionales para metadata del documento
  const metadataInputs: SerializedInput[] = [
    {
      name: "template_id",
      type: "STRING",
      value: templateId,
      order: fieldInputs.length,
    },
    {
      name: "template_name",
      type: "STRING",
      value: templateName,
      order: fieldInputs.length + 1,
    },
    {
      name: "template_version",
      type: "INTEGER",
      value: template?.version ?? 1,
      order: fieldInputs.length + 2,
    },
    {
      name: "file_name",
      type: "MIXED",
      value: variableName,
      order: fieldInputs.length + 2,
    },
    {
      name: "output_name",
      type: "MIXED",
      value: variableName,
      order: fieldInputs.length + 3,
    },
  ];

  // Combinar todos los inputs
  const allInputs = [...fieldInputs, ...metadataInputs];

  return {
    node_id: currentNode.id!,
    node: {
      display_name: title,
      flow_id: flowId,
      order: currentNode.order,
      position_x: currentNode.position_x,
      position_y: currentNode.position_y,
      ref_name: currentNode.ref_name,
      type: currentNode.type,
      manual_continue: manual_continue ?? false,
      inputs: allInputs,
    },
  };
};

// Export deserialization functions
export {
  getDeserializedFields,
  getSelectedTemplate,
  getFileName,
  getVariableName,
  getDeserializedDocumentGeneratorNode,
} from "./deserialization";
